// Phase HR-003 Wave 3 — Grievance API client.
import apiClient from '@/shared/api/client'

export type GrievanceSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
export type GrievanceStatus =
  | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED' | 'CLOSED'
export type GrievanceActionType =
  | 'RAISED' | 'ASSIGNED' | 'ESCALATED' | 'REPLIED'
  | 'RESOLVED' | 'REJECTED' | 'REOPENED' | 'CLOSED'

export interface GrievanceCreate {
  plant_id: string
  category_id: string
  description: string
  severity?: GrievanceSeverity
  is_anonymous?: boolean
  raised_by_name?: string | null
  department_id?: string | null
  shift_id?: string | null
  about_supervisor_id?: string | null
  evidence_url?: string | null
  witness_name?: string | null
}

export interface GrievanceAction {
  id: string
  action_at: string
  action_by_user_id: string | null
  action_by_name: string | null
  action_type: GrievanceActionType
  to_user_id: string | null
  to_user_name: string | null
  notes: string | null
  attachment_url: string | null
}

export interface Grievance {
  id: string
  grievance_no: string
  raised_at: string
  plant_id: string
  raised_by_employee_id: string | null
  raised_by_emp_code: string | null
  raised_by_name: string | null
  is_anonymous: boolean
  department_id: string | null
  department_name: string | null
  shift_id: string | null
  about_supervisor_id: string | null
  about_supervisor_name: string | null
  category_id: string
  category_name: string | null
  severity: GrievanceSeverity
  description: string
  evidence_url: string | null
  witness_name: string | null
  current_owner_user_id: string | null
  current_owner_name: string | null
  status: GrievanceStatus
  due_at: string | null
  is_overdue: boolean
  closed_at: string | null
  closure_summary: string | null
  closure_evidence_url: string | null
  satisfaction_score: number | null
  is_repeat_of_id: string | null
  actions: GrievanceAction[]
  closure_tat_hours: number | null
  created_at: string
}

export interface GrievanceListItem {
  id: string
  grievance_no: string
  raised_at: string
  raised_by_name: string | null
  is_anonymous: boolean
  department_id: string | null
  department_name: string | null
  category_name: string | null
  severity: GrievanceSeverity
  status: GrievanceStatus
  due_at: string | null
  is_overdue: boolean
  current_owner_name: string | null
  age_hours: number
}

export interface GrievanceActionInput {
  action_type: GrievanceActionType
  to_user_id?: string | null
  notes?: string | null
  attachment_url?: string | null
  closure_summary?: string | null
  closure_evidence_url?: string | null
  satisfaction_score?: number | null
}

export interface GrievanceStats {
  total: number
  open_count: number
  overdue_count: number
  closed_count: number
  avg_closure_tat_hours: number | null
  repeat_count: number
  repeat_pct: number
  by_category: Record<string, number>
  by_severity: Record<string, number>
  by_department: Record<string, number>
}

const BASE = '/hr/grievances'

export const grievanceApi = {
  raise: async (body: GrievanceCreate): Promise<Grievance> =>
    (await apiClient.post<Grievance>(BASE, body)).data,

  list: async (params: {
    plant_id?: string
    department_id?: string
    status_in?: GrievanceStatus[]
    severity?: GrievanceSeverity
    overdue_only?: boolean
    mine_only?: boolean
    limit?: number
  } = {}): Promise<GrievanceListItem[]> =>
    (await apiClient.get<GrievanceListItem[]>(BASE, { params })).data,

  listMine: async (): Promise<GrievanceListItem[]> =>
    (await apiClient.get<GrievanceListItem[]>(`${BASE}/mine`)).data,

  get: async (id: string): Promise<Grievance> =>
    (await apiClient.get<Grievance>(`${BASE}/${id}`)).data,

  postAction: async (id: string, body: GrievanceActionInput): Promise<Grievance> =>
    (await apiClient.post<Grievance>(`${BASE}/${id}/actions`, body)).data,

  stats: async (params: {
    plant_id?: string
    department_id?: string
    from_date?: string
    to_date?: string
  }): Promise<GrievanceStats> =>
    (await apiClient.get<GrievanceStats>(`${BASE}/stats`, { params })).data,
}

// Categories master is already served by /hr/grievance-categories — alias.
export interface GrievanceCategory {
  id: string
  category_code: string
  category_name: string
  default_severity: string
  target_resolution_hours: number
  requires_witness: boolean
  requires_evidence: boolean
  is_active: boolean
}

export const grievanceCategoryApi = {
  list: async (): Promise<GrievanceCategory[]> =>
    (await apiClient.get<GrievanceCategory[]>('/hr/grievance-categories')).data,
}
